import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiLock, FiCheckCircle } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteAdminUser, getAdminUsers } from 'apis/adminUsers';
import { useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const UsersList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [lockId, setLockId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const queryParams = useMemo(() => ({
        pageNumber: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
    }), [currentPage, pageSize, keyword, isActiveFilter]);

    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('users', queryParams, getAdminUsers);
    const deleteMutation = useAdminDeleteMutation('users', deleteAdminUser);

    useEffect(() => { setCurrentPage(1); }, [keyword, isActiveFilter]);
    useEffect(() => { if (error) toast.error(error.message || 'Tải dữ liệu thất bại'); }, [error, toast]);

    const handleLock = async () => {
        try {
            const res = await deleteMutation.mutateAsync(lockId);
            if (res?.success) {
                toast.success('Khóa tài khoản thành công');
            } else {
                toast.error(res?.message || 'Thao tác thất bại');
            }
        } catch {
            toast.error('Thao tác thất bại');
        }
        setLockId(null);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString('vi-VN');
    };

    const columns = [
        {
            header: 'ID',
            render: (row) => row.Id ?? row.id ?? '—',
        },
        {
            header: 'Tên đăng nhập',
            render: (row) => <span style={{ fontWeight: 600 }}>{row.Username || row.username || '—'}</span>,
        },
        {
            header: 'Họ và tên',
            render: (row) => row.FullName || row.fullName || '—',
        },
        {
            header: 'Email',
            render: (row) => row.Email || row.email || '—',
        },
        {
            header: 'Vai trò (Role)',
            render: (row) => {
                const roleObj = row.Roles || row.roles;
                const roleName = (roleObj?.Name || roleObj?.name || row.roleName || (row.RoleId === 1 || row.roleId === 1 ? 'admin' : (row.RoleId === 4 || row.roleId === 4 ? 'hr' : 'editor'))).toLowerCase();
                const badgeColor = roleName === 'admin' ? '#e74c3c' : roleName === 'hr' ? '#8e44ad' : '#27ae60';
                return (
                    <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        background: `${badgeColor}15`,
                        color: badgeColor,
                        fontWeight: 700,
                        fontSize: '12px',
                        textTransform: 'uppercase'
                    }}>
                        {roleName}
                    </span>
                );
            },
        },
        {
            header: 'Đăng nhập gần nhất',
            render: (row) => formatDate(row.LastLoginAt || row.lastLoginAt),
        },
        {
            header: 'Trạng thái',
            render: (row) => {
                const active = row.IsActive !== undefined ? row.IsActive : row.isActive;
                return (
                    <span className={`status-badge ${active ? 'active' : 'inactive'}`}>
                        {active ? 'Hoạt động' : 'Đã khóa'}
                    </span>
                );
            },
        },
        {
            header: 'Thao tác',
            render: (row) => {
                const id = row.Id ?? row.id;
                const active = row.IsActive !== undefined ? row.IsActive : row.isActive;
                return (
                    <div className="action-buttons">
                        <button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/users/edit/${id}`)} title="Sửa người dùng"><FiEdit2 size={15} /></button>
                        {active && (
                            <button className="icon-btn icon-btn--delete" onClick={() => setLockId(id)} title="Khóa tài khoản"><FiLock size={15} /></button>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Quản lý Người dùng"
                subtitle="Danh sách người dùng và phân quyền tài khoản (Chỉ Admin có quyền)"
                action={<Button onClick={() => navigate('/admin/users/create')}>+ Thêm Người dùng</Button>}
            />
            <div className="filters-bar">
                <div className="filter-item">
                    <Input
                        placeholder="Tìm theo username, email, tên..."
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                    />
                </div>
                <div className="filter-item">
                    <Select
                        value={isActiveFilter}
                        onChange={e => setIsActiveFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả trạng thái' },
                            { value: 'true', label: 'Hoạt động' },
                            { value: 'false', label: 'Đã khóa' },
                        ]}
                    />
                </div>
            </div>
            <Table
                columns={columns}
                data={data.items}
                loading={isFetching}
                pagination={{
                    currentPage: data.currentPage,
                    totalPages: Math.ceil(data.total / data.pageSize) || 1,
                    onPageChange: setCurrentPage,
                }}
            />
            <ConfirmationDialog
                open={!!lockId}
                onClose={() => setLockId(null)}
                onConfirm={handleLock}
                title="Khóa Tài khoản"
                message="Bạn có chắc chắn muốn khóa tài khoản người dùng này không?"
            />
        </div>
    );
};

export default UsersList;
